import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Landscaping = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my landscaping business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps landscaping businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for landscaping businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most landscaping businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my landscaping business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like landscaping. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Landscaping - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Landscaping CRM", url: "/industries/landscaping" }
  ];

  const industryData = {
    name: 'Landscaping',
    icon: '🌿',
    title: 'Stop Chasing Quotes & Start Landing Premium Landscaping Projects',
    subtitle: 'Showcase your portfolio, capture project requests 24/7, and automate seasonal maintenance contracts for predictable revenue',
    
    // Problem Section - Challenges they face
    challenges: [
      'Spending entire evenings measuring gardens and preparing detailed landscaping quotes',
      'Customers request quotes but never commit - you\'ve wasted hours on site visits',
      'Weather ruins your schedule and customers get frustrated with last-minute changes',
      'One-off garden projects only - no recurring maintenance income',
      'Can\'t answer calls when you\'re digging, mowing, or operating machinery',
      'Completed stunning gardens but customers never share reviews or referrals',
      'Losing commercial contracts to larger firms with better presentation',
      'Seasonal slowdowns mean unpredictable cash flow in winter months'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      'Online project request form with photo upload lets customers share garden images and describe their vision 24/7 - qualify leads before site visits',
      'Interactive project calculator provides rough pricing for common projects (patios, decking, artificial lawns, garden design) before you invest time in quotes',
      'Automated seasonal maintenance packages convert one-off customers into recurring monthly clients with lawn care, hedge trimming, and garden upkeep',
      'Weather update automation sends SMS alerts about rain delays or rescheduling so customers aren\'t left wondering',
      'Before/after photo showcase gallery displays your best landscaping transformations to win premium projects',
      'Commercial proposal templates help you pitch to property managers, housing associations, and businesses for grounds maintenance contracts',
      'Review automation requests 5-star Google reviews after every project completion with portfolio photo sharing'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      'Project Request Form with Photo Upload (24/7 capture)',
      'Landscaping Project Cost Calculator (patios, decking, artificial lawns)',
      'Before/After Transformation Photo Gallery',
      'Seasonal Maintenance Package Builder & Automation',
      'Monthly Lawn Care Subscription System',
      'Automated Weather Delay SMS Notifications',
      'Online Booking Calendar for Consultations',
      'Commercial Grounds Maintenance Proposal Templates',
      'Garden Design Mood Board Portal (share inspiration images)',
      'Project Progress Update Automation (SMS & Email)',
      'Google Review Request Automation with Photo Prompts',
      'Service Area Mapper (coverage postcodes)',
      'Equipment & Materials Cost Tracker',
      'Customer Portal with Project Photos & Invoices'
    ],
    
    // Results Section - Success metrics
    results: [
      '65% quote conversion improvement',
      '£4.8K average monthly recurring revenue added',
      '3.5x more premium projects won'
    ],
    
    // SEO Section
    metaTitle: 'Landscaping Business Growth Platform | Win More Premium Garden Projects',
    metaDescription: 'Stop chasing landscaping quotes. Automated lead capture, project calculators, seasonal maintenance subscriptions & before/after showcase gallery.',
    
    // Reviews Section
    reviews: [
      {
        author: 'Tom H.',
        business: 'Garden Design & Landscaping',
        location: 'Surrey',
        rating: 5,
        text: 'The project request form with photo upload is incredible. Customers share garden photos and describe what they want before I even visit. I can now qualify leads remotely and only visit properties that are genuinely a good fit. My quote conversion went from 25% to 64%, and I\'m winning way more premium projects.',
        verified: true
      },
      {
        author: 'Rachel M.',
        business: 'Landscaping & Garden Maintenance',
        location: 'Kent',
        rating: 5,
        text: 'The seasonal maintenance automation changed our business model. We now convert one-off patio installations into ongoing monthly lawn care clients. Added £4,200 per month in recurring revenue in just 5 months. The automated reminders mean customers never forget to book spring tidy-ups or autumn leaf clearance.',
        verified: true
      },
      {
        author: 'Paul S.',
        business: 'Commercial & Residential Landscaping',
        location: 'Hampshire',
        rating: 5,
        text: 'Secured 3 commercial grounds maintenance contracts worth £92K+ using the proposal templates and before/after photo gallery. Property managers want professional presentations, and this system makes us look like a much bigger operation. The weather delay SMS notifications are brilliant too - no more frustrated clients.',
        verified: true
      },
      {
        author: 'Emma J.',
        business: 'Landscape Gardening Services',
        location: 'Berkshire',
        rating: 5,
        text: 'Never miss a lead now even when I\'m on-site all day. The project calculator gives customers instant ballpark pricing for patios and decking, and the automated follow-ups turn inquiries into bookings. Our Google reviews went from 11 to 52 in 6 months thanks to the review request automation with portfolio photos.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Landscaping UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for landscaping businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for landscaping UK, landscaping business software, landscaping customer management, landscaping lead generation UK"
        url="/industries/landscaping"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Landscaping;